/* ═══════════════════════════════════════════════════════════════
   Spec Masters Studio — the spec commands the Designer does not
   already own.

   Everything the Designer HAS is imported, never re-written: level
   changes, duplicate, delete, insert table / image, skip-at-export,
   find, unique ids, and the whole keymap all come from
   `designer/commands.ts`. What lives here is the handful of master-
   authoring operations BlockEditor had and the Designer never needed:

     · continuation blocks (share the previous number)   [C1]
     · move a block (or a multi-selection) up / down     [B6]
     · add an empty block below, at the same level       [B1]
     · select a block WITH its sub-items (the subtree)   [B10]
     · resize a figure                                    [I2]

   Each one is a ProseMirror transaction, so one Ctrl+Z reverses it.
   ═══════════════════════════════════════════════════════════════ */

import { Extension } from '@tiptap/core';
import type { Editor } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import { Fragment, Slice } from '@tiptap/pm/model';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import {
  BLOCK_NODE_NAMES,
  deleteSelectedBlocks,
  expandToGroups,
  groupIdOf,
  selectedBlocks,
} from '../designer/commands';
import { newBlockId, newGroupId } from '../designer/schema';
import { createGroupRemap } from '../../shared/block-groups';
import { stampOfNode } from '../designer/moduleInsert';
import { blockSelectKey, blockSelection, moveBlocksTo, nudgeTarget } from './blockSelect';
import { deleteOrHideSelectedBlocks } from './variantCommands';
import { activeVariantView } from './variantPlugin';
import { modulePartHeadersKey, moveModuleSelectionBy } from './moduleBuild';

type Dispatch = ((tr: Transaction) => void) | undefined;

/**
 * "No number" (internally still the `continuation` attribute): the paragraph
 * shares the previous one's number and prints without a prefix of its own.
 * One toggle for the whole selection — if any touched block is a plain
 * paragraph, they all become no-number blocks.
 *
 * Allowed on the first block too (Phase 1 repair, docs/MASTERS_UNIFIED_PLAN.md
 * §Phase 1.1) — the engine renders it with no prefix regardless of position,
 * so there is nothing left that requires a previous number to share.
 */
export function toggleContinuation(state: EditorState, dispatch: Dispatch): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const next = sel.some((b) => !b.node.attrs.continuation);
  let tr: Transaction | null = null;
  for (const b of sel) {
    if (!!b.node.attrs.continuation === next) continue;
    tr = (tr || state.tr).setNodeAttribute(b.pos, 'continuation', next);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/** Every top-level block, in order, with its position. */
function allBlocks(state: EditorState): Array<{ node: PMNode; pos: number }> {
  const out: Array<{ node: PMNode; pos: number }> = [];
  state.doc.forEach((node, offset) => {
    if (BLOCK_NODE_NAMES.includes(node.type.name)) out.push({ node, pos: offset });
  });
  return out;
}

/**
 * Move the selected block (or contiguous multi-selection) one place up or
 * down, taking the caret with it — Word's Alt+Shift+Arrow. The run is cut and
 * re-inserted in one transaction so Ctrl+Z puts it back in one step.
 */
export function moveSelectedBlocks(state: EditorState, dispatch: Dispatch, delta: -1 | 1): boolean {
  const sel = selectedBlocks(state);
  if (!sel.length) return false;
  const ids = sel.map((b) => (b.node.attrs.blockId as string) || '').filter(Boolean);
  if (!ids.length) return false;
  // The reorder itself is `blockSelect.moveBlocksTo` — the SAME transaction a
  // gutter drag runs, so keyboard and pointer can never drift apart, and a
  // non-contiguous selection lands as one run instead of dragging the
  // untouched blocks between it along (Phase 1 item 4).
  // A MODULE canvas walks its parts the way its gutter drag does (TWEAKS
  // item 10): across a part boundary, into an empty part, re-tagging an
  // article with the part it lands in.
  if (modulePartHeadersKey.getState(state) !== undefined) {
    return moveModuleSelectionBy(state, dispatch, ids, delta);
  }
  const target = nudgeTarget(state, ids, delta);
  if (target == null) return false;
  return moveBlocksTo(state, dispatch, ids, target);
}

/** An empty paragraph right below the selection, at the same level — the
 *  mouse-driven twin of pressing Enter at the end of the line. */
export function addBlockBelow(state: EditorState, dispatch: Dispatch): boolean {
  const sel = selectedBlocks(state);
  const last = sel.length ? sel[sel.length - 1] : null;
  const insertAt = last ? last.pos + last.node.nodeSize : state.doc.content.size;
  const level = last ? (last.node.attrs.level as number) || 1 : 1;
  const node = state.schema.nodes.specParagraph.create({
    blockId: newBlockId(),
    level,
    continuation: false,
    excluded: false,
    meta: {},
  });
  const tr = state.tr.insert(insertAt, node);
  tr.setSelection(TextSelection.near(tr.doc.resolve(insertAt + 1)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Select the block the caret sits in TOGETHER with everything nested under
 * it — BlockEditor's drag-handle "select the subtree" behaviour, kept because
 * moving a PART with its articles is the whole point of an outline.
 */
export function selectSubtree(state: EditorState, dispatch: Dispatch): boolean {
  const all = allBlocks(state);
  const sel = selectedBlocks(state);
  if (!sel.length || !all.length) return false;
  const startIdx = all.findIndex((b) => b.pos === sel[0].pos);
  if (startIdx < 0) return false;
  const level = (all[startIdx].node.attrs.level as number) || 1;
  let endIdx = startIdx;
  for (let i = startIdx + 1; i < all.length; i++) {
    const lvl = (all[i].node.attrs.level as number) || 1;
    if (lvl <= level) break;
    endIdx = i;
  }
  const from = all[startIdx].pos;
  const to = all[endIdx].pos + all[endIdx].node.nodeSize;
  // `between` snaps each end to the nearest position that can actually hold a
  // text selection, so a subtree that starts or ends on an atom (a figure, a
  // table) selects cleanly instead of throwing.
  const tr = state.tr.setSelection(
    TextSelection.between(state.doc.resolve(from), state.doc.resolve(to))
  );
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/** Print width of the selected figure, 20–100% of the text column. */
export function setImageWidth(state: EditorState, dispatch: Dispatch, pct: number): boolean {
  const width = Math.max(20, Math.min(100, Math.round(pct)));
  const sel = selectedBlocks(state).filter((b) => b.node.type.name === 'specImage');
  if (!sel.length) return false;
  let tr: Transaction | null = null;
  for (const b of sel) {
    if (b.node.attrs.imageWidth === width) continue;
    tr = (tr || state.tr).setNodeAttribute(b.pos, 'imageWidth', width);
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/** The figure the selection is on, if any — drives the width control. */
export function selectedImage(state: EditorState): { pos: number; width: number } | null {
  const sel = selectedBlocks(state).filter((b) => b.node.type.name === 'specImage');
  if (sel.length !== 1) return null;
  return { pos: sel[0].pos, width: (sel[0].node.attrs.imageWidth as number) ?? 100 };
}

/** Put the caret in a block by document position, selecting atoms whole —
 *  what the outline's rows and the "jump to" paths need. */
export function focusBlockAt(state: EditorState, dispatch: Dispatch, pos: number): boolean {
  if (pos < 0 || pos > state.doc.content.size) return false;
  const node = state.doc.nodeAt(pos);
  const tr = node?.type.isAtom
    ? state.tr.setSelection(NodeSelection.create(state.doc, pos))
    : state.tr.setSelection(TextSelection.near(state.doc.resolve(pos + 1)));
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/* ═══════════════════════════════════════════════════════════════
   GROUPS (Spec Studio Tables + Top Bar plan, T3).

   A group is nothing but a shared `groupId` on a run of blocks. It is
   FLAT (one level — a block belongs to at most one group), CONTIGUOUS
   (the commands here and `moveBlocksTo` keep it so), and it changes
   nothing about a block's level, its numbering or its export: the
   outline panel and the .docx builder never read it.

   Grouping earns its keep through `expandToGroups` — naming one block
   of a group names them all — so move, duplicate, delete, level,
   continuation, skip-at-export, hide-in-variant and the clipboard triad
   act on the whole run with no code of their own.
   ═══════════════════════════════════════════════════════════════ */

/** Why a group cannot be made right now. `few` = fewer than two blocks
 *  picked; `module` = it would cut a module insertion in half. */
export type GroupRefusal = 'few' | 'module';

export const GROUP_MODULE_REFUSAL = "Groups can't split a module";

export interface GroupPlan {
  /** The blocks that would be grouped — document order, whole groups. */
  ids: string[];
  refusal: GroupRefusal | null;
}

/** Every top-level block with its id, in document order. */
function blockRefs(state: EditorState): Array<{ id: string; node: PMNode; pos: number }> {
  return allBlocks(state).map((b) => ({ ...b, id: (b.node.attrs.blockId as string) || '' }));
}

/**
 * What Ctrl+G would do, and why it would refuse — read by the palette and the
 * menu for their disabled states, and by the command itself, so a button's
 * tooltip can never disagree with what the key does.
 *
 * The module rule: a module insertion is ONE thing in the document (Phase 5's
 * stamp), so a group may take a whole insertion along with its neighbours but
 * never half of one. Refusing is the honest answer — the alternative is a
 * group whose contiguity and a module run's contiguity contradict each other
 * on the next move.
 */
export function planGroup(state: EditorState): GroupPlan {
  const picked = blockSelection(state).ids;
  const want = expandToGroups(state.doc, picked);
  const refs = blockRefs(state);
  const ids = refs.filter((b) => want.has(b.id)).map((b) => b.id);
  if (ids.length < 2) return { ids, refusal: 'few' };

  const inSel = new Set(ids);
  const wantedRuns = new Set<string>();
  for (const b of refs) {
    if (!inSel.has(b.id)) continue;
    const stamp = stampOfNode(b.node);
    if (stamp) wantedRuns.add(stamp.insertId);
  }
  for (const b of refs) {
    const stamp = stampOfNode(b.node);
    if (!stamp || !wantedRuns.has(stamp.insertId)) continue;
    if (!inSel.has(b.id)) return { ids, refusal: 'module' };
  }
  return { ids, refusal: null };
}

/**
 * Group the picked blocks. A scattered selection is made CONTIGUOUS first —
 * the run gathers at the first picked block, exactly the way a gutter drag
 * gathers it (`moveBlocksTo`'s semantics) — and the whole thing is ONE
 * transaction, so one Ctrl+Z ungroups AND puts the order back.
 *
 * Blocks that were already grouped come in whole (`expandToGroups`) and MERGE
 * into the new group: grouping stays one level deep, always.
 */
export function groupSelectedBlocks(state: EditorState, dispatch: Dispatch): GroupRefusal | null {
  const plan = planGroup(state);
  if (plan.refusal) return plan.refusal;

  const refs = blockRefs(state);
  const want = new Set(plan.ids);
  const moving = refs.filter((b) => want.has(b.id));
  const firstIdx = refs.indexOf(moving[0]);
  const contiguous = moving.every((b, i) => refs[firstIdx + i] === b);
  const gid = newGroupId();

  const tr = state.tr;
  if (contiguous) {
    // Nothing to move — stamp them where they stand and leave the caret alone.
    for (const b of moving) tr.setNodeAttribute(b.pos, 'groupId', gid);
  } else {
    // Cut LAST first so earlier positions stay valid, then re-insert the run
    // — already stamped — at the gap the first picked block left behind.
    const nodes = moving.map((b) =>
      b.node.type.create({ ...b.node.attrs, groupId: gid }, b.node.content, b.node.marks)
    );
    const anchorPos = refs[firstIdx].pos;
    for (let i = moving.length - 1; i >= 0; i--) {
      tr.delete(moving[i].pos, moving[i].pos + moving[i].node.nodeSize);
    }
    tr.insert(tr.mapping.map(anchorPos, -1), nodes);
  }
  // The group stays picked: the user is usually about to move it or act on it.
  tr.setMeta(blockSelectKey, { ids: plan.ids, anchor: plan.ids[0] });
  if (dispatch) dispatch(tr.scrollIntoView());
  return null;
}

/** Whether the selection holds any group at all — the Ungroup gate. */
export function selectionHasGroup(state: EditorState): boolean {
  const want = expandToGroups(state.doc, blockSelection(state).ids);
  if (!want.size) return false;
  return blockRefs(state).some((b) => want.has(b.id) && !!groupIdOf(b.node));
}

/**
 * Ungroup every group the selection touches — the blocks stay exactly where
 * they are, they simply stop travelling together. One transaction, one Ctrl+Z.
 */
export function ungroupSelectedBlocks(state: EditorState, dispatch: Dispatch): boolean {
  const want = expandToGroups(state.doc, blockSelection(state).ids);
  if (!want.size) return false;
  const refs = blockRefs(state);
  const groups = new Set<string>();
  for (const b of refs) {
    if (!want.has(b.id)) continue;
    const gid = groupIdOf(b.node);
    if (gid) groups.add(gid);
  }
  if (!groups.size) return false;
  let tr: Transaction | null = null;
  for (const b of refs) {
    if (!groups.has(groupIdOf(b.node))) continue;
    tr = (tr || state.tr).setNodeAttribute(b.pos, 'groupId', '');
  }
  if (!tr) return false;
  if (dispatch) dispatch(tr);
  return true;
}

/* ═══════════════════════════════════════════════════════════════
   THE BLOCK CLIPBOARD (Table Standard contract, Tranche 4).

   Two modes, ONE rule: the gutter block selection is the switch.

     · blocks handle-selected  → Delete / Ctrl+C / Ctrl+X / Ctrl+V act
       on WHOLE blocks;
     · nothing handle-selected → every one of those keys returns false
       and ProseMirror's own text behaviour runs, untouched. The caret,
       Backspace, and native copy/paste are not re-implemented here.

   The payload is ProseMirror's OWN clipboard format — the view's
   serializer writes `text/html` (carrying `data-pm-slice="0 0 []"`, so a
   CLOSED slice round-trips closed) plus the `text/plain` fallback that
   makes a paste into Word or Notepad read as the paragraphs it is. No
   custom format, no new dependency, and cross-spec paste comes free
   because the OS clipboard is the transport.
   ═══════════════════════════════════════════════════════════════ */

/** The blocks a clipboard command acts on: the GUTTER selection, and only
 *  the gutter selection. `null` means "not block mode", which every caller
 *  turns into `return false` — the switch itself. */
function pickedBlocks(state: EditorState): Array<{ node: PMNode; pos: number }> | null {
  if (!blockSelection(state).ids.length) return null;
  // `selectedBlocks` resolves the same ids; asking IT rather than walking the
  // document again is what keeps block copy, block delete and block level
  // changes standing on one definition of "the blocks I mean".
  const sel = selectedBlocks(state);
  return sel.length ? sel : null;
}

/** Whether the gutter holds a live selection — exported so the menu and the
 *  gutter handles route to the same two modes the keymap does. */
export function hasBlockSelection(state: EditorState): boolean {
  return pickedBlocks(state) !== null;
}

/** The selected blocks as a CLOSED slice, in document order. Closed (open
 *  depth 0 at both ends) is what makes a paste land as whole blocks instead
 *  of merging its first paragraph into the destination's — and a
 *  non-contiguous Ctrl+click selection becomes one closed slice of exactly
 *  those blocks. */
function blockSlice(state: EditorState): Slice | null {
  const sel = pickedBlocks(state);
  if (!sel) return null;
  return new Slice(Fragment.from(sel.map((b) => b.node)), 0, 0);
}

/** Put an already-serialized block slice on the event's clipboard. Split out
 *  of `copyBlocks` so a CUT can serialize (pure) up front, run its delete, and
 *  only then write — a cut that the document refuses must leave the user's
 *  clipboard exactly as it found it. */
function writeSlice(view: EditorView, event: ClipboardEvent, slice: Slice): boolean {
  const data = event.clipboardData;
  if (!data) return false;
  const { dom, text } = view.serializeForClipboard(slice);
  data.clearData();
  data.setData('text/html', dom.innerHTML);
  data.setData('text/plain', text);
  event.preventDefault();
  return true;
}

/** Ctrl+C on a block selection. Returns false — and touches nothing — when
 *  there is no block selection, so the native copy runs exactly as today. */
export function copyBlocks(view: EditorView, event: ClipboardEvent): boolean {
  const slice = blockSlice(view.state);
  if (!slice) return false;
  // The selection STAYS: the user copied, they did not move anything.
  return writeSlice(view, event, slice);
}

/**
 * Ctrl+X = the delete, and the delete is one transaction — so one Ctrl+Z puts
 * every cut block back where it was.
 *
 * The delete runs FIRST and the clipboard is written only once it has actually
 * landed. In a VARIANT the plugin's `filterTransaction` refuses a removal
 * (`variantPlugin`, reason `'remove'`), and a cut that cannot remove must not
 * silently degrade into a copy: the document is unchanged, so the clipboard is
 * left unchanged too, and the event is swallowed so no native cut can carve
 * text out from under the block selection. One no-op, and the variant's own
 * gate toast explains it.
 */
export function cutBlocks(view: EditorView, event: ClipboardEvent): boolean {
  if (!view.editable) return false;
  const slice = blockSlice(view.state);
  if (!slice) return false;
  const before = view.state.doc;
  const deleted = deleteSelectedBlocks(view.state, view.dispatch.bind(view));
  if (!deleted || view.state.doc === before) {
    // Refused (or nothing to delete): a strict no-op — no clipboard write.
    event.preventDefault();
    return true;
  }
  return writeSlice(view, event, slice);
}

/** Is this slice a run of WHOLE top-level blocks? Closed at both ends and
 *  every child a block node — anything else (a text run, half a paragraph,
 *  HTML from a web page) is left to ProseMirror. */
export function isBlockPayload(slice: Slice): boolean {
  if (slice.openStart !== 0 || slice.openEnd !== 0) return false;
  const { content } = slice;
  if (!content.childCount) return false;
  for (let i = 0; i < content.childCount; i++) {
    if (!BLOCK_NODE_NAMES.includes(content.child(i).type.name)) return false;
  }
  return true;
}

/** `meta` is a block's bookkeeping bag. An object survives a clipboard
 *  round-trip only as far as a DOM attribute can carry one — i.e. not at
 *  all — so a pasted block gets an empty bag rather than the string
 *  "[object Object]" the browser would otherwise hand back. Level,
 *  continuation and skip-at-export are scalars and DO survive. */
function safeMeta(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

/**
 * Ctrl+V of a block payload: the blocks land WHOLE, after the last
 * handle-selected block if a block selection is active, else after the block
 * the caret sits in — the same landing `duplicateSelectedBlocks` (Ctrl+D) and
 * `addBlockBelow` (Ctrl+Enter) use, so the variant guard (`filterTransaction`)
 * and the module part stamp (`transformPasted`, which has already run by the
 * time a slice reaches `handlePaste`) both apply with no second code path.
 * Fresh ids are minted immediately — two blocks must never claim one
 * identity — and the pasted run BECOMES the block selection, so move, delete
 * and paste-again chain straight off it.
 *
 * Block mode needs BOTH halves of the switch to be true: blocks picked in the
 * gutter, or — with nothing picked — a bare caret. A live TEXT selection means
 * the user asked for a replacement, and a replacement is ProseMirror's job:
 * Ctrl+A then Ctrl+V (an `AllSelection`, whose slice is closed and so reads as
 * a block payload) must REPLACE the document, not append a second copy of it
 * after the last block, and the same goes for a `NodeSelection` on a figure or
 * for any run of selected text. Non-empty selection ⇒ return false ⇒ native
 * paste replaces exactly what is selected.
 */
export function pasteBlocks(state: EditorState, dispatch: Dispatch, slice: Slice): boolean {
  if (!blockSelection(state).ids.length && !state.selection.empty) return false;
  if (!isBlockPayload(slice)) return false;
  const sel = selectedBlocks(state);
  const last = sel.length ? sel[sel.length - 1] : null;
  const insertAt = last ? last.pos + last.node.nodeSize : state.doc.content.size;
  const nodes: PMNode[] = [];
  const ids: string[] = [];
  // A COPY never joins the group it was copied from — the pasted run gets a
  // group of its own, so ungrouping the copy can never touch the original.
  // One fresh id per group in the payload: copying two groups pastes two.
  // `createGroupRemap` is the ONE remap every landing path shares (the module
  // insert uses it too), so paste and insert can never disagree about it.
  const remapGroup = createGroupRemap(newGroupId);
  slice.content.forEach((node) => {
    const blockId = newBlockId();
    ids.push(blockId);
    nodes.push(
      node.type.create(
        {
          ...node.attrs,
          blockId,
          groupId: remapGroup(node.attrs.groupId as string),
          meta: safeMeta(node.attrs.meta),
        },
        node.content,
        node.marks
      )
    );
  });
  if (!nodes.length) return false;
  const tr = state.tr.insert(insertAt, nodes);
  // Same transaction as the insert, so the new selection can never be a frame
  // behind the blocks it names. The ids are already in document order — they
  // land contiguously — which is the order the plugin stores.
  tr.setMeta(blockSelectKey, { ids, anchor: ids[0] });
  if (dispatch) dispatch(tr.scrollIntoView());
  return true;
}

/**
 * Paste from the SYSTEM clipboard on demand — the path for surfaces that are
 * not the editor's own keydown: the right-click menu's Paste and a gutter
 * handle's Ctrl+V. `pasteHTML` / `pasteText` are prosemirror-view's public
 * entries into the very same paste pipeline the key takes, so a block payload
 * is recognised by `handlePaste` exactly as it would have been.
 */
export async function pasteFromSystemClipboard(view: EditorView): Promise<boolean> {
  if (!view.editable) return false;
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (!item.types.includes('text/html')) continue;
      const html = await (await item.getType('text/html')).text();
      if (!html) continue;
      view.focus();
      return view.pasteHTML(html);
    }
    const text = await navigator.clipboard.readText();
    if (text) {
      view.focus();
      return view.pasteText(text);
    }
  } catch {
    // Permission denied — Ctrl+V inside the document still pastes natively.
  }
  return false;
}

/** Focus the sheet and let the platform raise a real clipboard event, which
 *  the plugin below answers. Used by the gutter handles — portalled buttons,
 *  so the editor's own keymap never sees their keydown — and by the menu. */
export function runClipboardCommand(view: EditorView, command: 'copy' | 'cut'): boolean {
  if (command === 'cut' && !view.editable) return false;
  view.focus();
  return document.execCommand(command);
}

/**
 * The menu's Cut on a PICKED ATOM (a figure, a Related sections block). The
 * browser never offers a native cut on a non-editable node, so
 * `execCommand('cut')` never reaches `cutBlocks`; this is the same cut by
 * hand, with the same law: the delete is checked FIRST (a variant refusing
 * the removal leaves the clipboard untouched), then the block copy writes the
 * clipboard, then the one-step delete lands — one Ctrl+Z brings it back.
 */
export function cutPickedAtom(view: EditorView): boolean {
  if (!view.editable) return false;
  let tr: Transaction | null = null;
  deleteSelectedBlocks(view.state, (t) => (tr = t));
  if (!tr) return false;
  const pending: Transaction = tr;
  if (view.state.applyTransaction(pending).state.doc === view.state.doc) return true;
  view.focus();
  if (!document.execCommand('copy')) return false;
  view.dispatch(pending);
  return true;
}

export const blockClipboardKey = new PluginKey('specStudioBlockClipboard');

/** The whole feature in one extension: the two keys that delete, and the
 *  three DOM events that carry the clipboard. Registered by the Spec Studio
 *  canvas only — the Designer never installs the block selection plugin, so
 *  `pickedBlocks` is always null there and nothing here can fire. */
export const BlockClipboard = Extension.create({
  name: 'specStudioBlockClipboard',

  addKeyboardShortcuts() {
    const deleteBlocks = ({ editor }: { editor: Editor }) => {
      if (!editor.isEditable) return false;
      if (!hasBlockSelection(editor.state)) return false;
      // Inside a variant a baseline (office-master) block in the selection
      // cannot be removed — it is hidden instead (T4); `activeVariantView` is
      // null outside a variant, where this is exactly `deleteSelectedBlocks`.
      const view = activeVariantView(editor.state);
      const isBaseline = view ? (id: string) => view.baselineIds.has(id) : null;
      return deleteOrHideSelectedBlocks(
        editor.state,
        editor.view.dispatch.bind(editor.view),
        isBaseline
      );
    };
    // Backspace as well as Delete: a writer who picked three paragraphs by
    // their handles means the same thing with either key. With NO block
    // selection both return false and ProseMirror's own merge/join runs.
    return { Delete: deleteBlocks, Backspace: deleteBlocks };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: blockClipboardKey,
        props: {
          // Consulted BEFORE prosemirror-view's own copy/cut handlers, and
          // only ever answers in block mode — so the native clipboard is
          // untouched everywhere else.
          handleDOMEvents: {
            copy: (view, event) => copyBlocks(view, event),
            cut: (view, event) => cutBlocks(view, event),
          },
          handlePaste: (view, _event, slice) =>
            view.editable ? pasteBlocks(view.state, view.dispatch.bind(view), slice) : false,
        },
      }),
    ];
  },
});
